import { useEffect, useState } from 'react';
import HTMLParser from 'html-react-parser';
import { PaymentMethodDto, RichTextModel } from '@/services/platform/api/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Switch } from '@/components/ui/switch';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { cn, useClientTranslation } from '@/lib/core';
import { formatCardBrand, maskExpiry, parseExpiry, toExpiryInput } from './utils';

interface ProfilePaymentEditProps {
  paymentMethod: PaymentMethodDto | null;
  /** Sheet heading — `updateCardTitle` from the CMS where available. */
  title: string | undefined;
  privacyPolicyDisclaimer?: RichTextModel;
  onOpenChange: (open: boolean) => void;
  /** Rejects (throws) when the update fails, so the sheet can keep the form open and report it. */
  onUpdate: (expMonth: number, expYear: number, holderName: string, isPreferred: boolean) => Promise<void>;
  /** Rejects (throws) when the delete fails. */
  onDelete: () => Promise<void>;
}

/**
 * "Update payment method" bottom sheet.
 *
 * Only the expiry and cardholder name are editable, because those are the only fields
 * `UpdatePaymentMethodRequest` accepts — Stripe permits no change to a saved card's number or CVC.
 * The card number is therefore read-only text (`last4` is the one part Stripe returns), and
 * replacing a number means adding a new card.
 *
 * There is deliberately **no CVV field**, which diverges from Figma 2952:79811. Stripe never stores
 * or returns the CVC, so there is nothing to display read-only and nothing to send; requiring it
 * would only block an otherwise-valid name or expiry correction.
 */
export function ProfilePaymentEdit({
  paymentMethod,
  title,
  privacyPolicyDisclaimer,
  onOpenChange,
  onUpdate,
  onDelete,
}: ProfilePaymentEditProps) {
  const t = useClientTranslation();
  const [expiry, setExpiry] = useState('');
  const [holderName, setHolderName] = useState('');
  const [isPreferred, setIsPreferred] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [expiryTouched, setExpiryTouched] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed the fields whenever a different card is opened.
  useEffect(() => {
    if (!paymentMethod) return;
    setExpiry(toExpiryInput(paymentMethod));
    setHolderName(paymentMethod.holderName ?? '');
    setIsPreferred(paymentMethod.isPreferred ?? false);
    setExpiryTouched(false);
    setSubmitError(null);
  }, [paymentMethod]);

  const { value: parsedExpiry, error: expiryError } = parseExpiry(expiry);
  const isValid = !!parsedExpiry && !!holderName.trim();

  // A card that is already preferred cannot be un-preferred: the API exposes only
  // `POST /me/payment-methods/{id}/preferred` — there is no "unset", and the parent's `onUpdate`
  // consequently ignores `isPreferred: false`. Leaving the switch live would let the user turn it
  // off, see the success toast, and find the card still preferred. The flag moves by promoting a
  // different card, so the switch stays live on every card that is *not* currently preferred.
  const preferredLocked = paymentMethod?.isPreferred === true;

  // A complete-but-unusable expiry (e.g. a card that already expired) leaves the CTA active so the
  // tap can surface *why* it fails — a greyed-out button with no explanation is a dead end. Only a
  // genuinely unfinished form disables it, which is the state the design greys out.
  const isSubmittable = isValid || (expiryError === 'past' && !!holderName.trim());

  // An already-expired saved card seeds the form with a past date, so the error only shows once the
  // user has engaged with the field or attempted to save — not the instant the sheet opens.
  const expiryMessage =
    expiryTouched && expiryError
      ? expiryError === 'past'
        ? t('App.Labels.ExpiryDateInPast')
        : t('App.Labels.ExpiryDateInvalid')
      : undefined;

  async function handleUpdate() {
    setExpiryTouched(true);
    if (!isValid || saving || !parsedExpiry) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await onUpdate(parsedExpiry.expMonth, parsedExpiry.expYear, holderName.trim(), isPreferred);
    } catch {
      // The parent throws on a non-204; keep the sheet open so the user can correct and retry.
      setSubmitError(t('App.Labels.PaymentMethodUpdateError'));
    } finally {
      setSaving(false);
    }
  }

  const cardLabel = paymentMethod
    ? t('App.Labels.CardEndingIn', {
        brand: formatCardBrand(paymentMethod.brand),
        last4: paymentMethod.last4 ?? '',
      })
    : '';

  return (
    <>
      <Drawer open={!!paymentMethod} onOpenChange={onOpenChange} showSwipeHandle={false}>
        <DrawerContent className="max-h-[92dvh]">
          <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 px-4 py-8 text-center text-[14px] tracking-[0.5px] uppercase">
              {title ?? t('App.Labels.UpdatePaymentMethod')}
            </p>

            <div className="flex flex-col gap-4 px-4 pb-8">
              <FormField id="edit-card-number" label={t('App.Labels.PaymentCardNumber')} required>
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex h-10 items-center px-3.75 text-[14px] tracking-[0.5px]">
                  {cardLabel}
                </p>
              </FormField>

              <FormField
                id="edit-card-expiry"
                label={t('App.Labels.ExpirationDate')}
                required
                error={expiryMessage}
              >
                <Input
                  id="edit-card-expiry"
                  value={expiry}
                  inputMode="numeric"
                  placeholder={t('App.Labels.ExpirationDatePlaceholder')}
                  error={!!expiryMessage}
                  onBlur={() => setExpiryTouched(true)}
                  onChange={event => {
                    setExpiry(maskExpiry(event.target.value));
                    setSubmitError(null);
                  }}
                />
              </FormField>

              <FormField id="edit-holder-name" label={t('App.Labels.NameOnCard')} required>
                <Input
                  id="edit-holder-name"
                  value={holderName}
                  placeholder={t('App.Labels.NameOnCardPlaceholder')}
                  autoComplete="cc-name"
                  onChange={event => setHolderName(event.target.value)}
                />
              </FormField>

              <label className={cn('flex items-start gap-2 pt-2', preferredLocked && 'cursor-default')}>
                <Switch
                  checked={isPreferred}
                  onCheckedChange={setIsPreferred}
                  disabled={preferredLocked}
                  className="mt-0.5"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p
                    className={cn(
                      'font-pp-neue-montreal text-[14px] tracking-[0.5px]',
                      preferredLocked ? 'text-jncquoi-neutral-700' : 'text-jncquoi-neutral-1000'
                    )}
                  >
                    {t('App.Labels.PreferredPaymentMethod')}
                  </p>
                  {preferredLocked && (
                    <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px]">
                      {t('App.Labels.PreferredPaymentMethodLocked')}
                    </p>
                  )}
                </div>
              </label>

              {privacyPolicyDisclaimer?.markup && (
                <div className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] [&_a]:font-semibold [&_a]:underline">
                  {HTMLParser(privacyPolicyDisclaimer.markup)}
                </div>
              )}
            </div>
          </div>

          <div
            className="border-jncquoi-neutral-400 shrink-0 border-t-[0.5px] px-4 pt-6"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
          >
            {submitError && (
              <p
                role="alert"
                className="font-pp-neue-montreal text-jncquoi-asia-7427c-500 mb-3 text-center text-xs tracking-[0.5px]"
              >
                {submitError}
              </p>
            )}
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              className="w-full"
              disabled={!isSubmittable || saving}
              loading={saving}
              onClick={handleUpdate}
            >
              {t('App.Labels.UpdatePaymentMethod')}
            </Button>
            <Button
              variant="tertiary-link"
              palette="dark"
              size="sm"
              className="mt-1 w-full"
              onClick={() => setShowDelete(true)}
            >
              {t('App.Labels.Delete')}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/*
        `ConfirmationSheet` renders its own error state when `onConfirm` rejects, so a failed
        delete is reported inside the sheet and the card stays put.
      */}
      <ConfirmationSheet
        open={showDelete}
        onOpenChange={setShowDelete}
        onConfirm={onDelete}
        errorText={t('App.Labels.PaymentMethodDeleteError')}
        headerTitle={t('App.Labels.Confirmation')}
        title={t('App.Labels.DeletePaymentMethodTitle')}
        description={t('App.Labels.DeletePaymentMethodDescription')}
        confirmLabel={t('App.Labels.Delete')}
        dismissLabel={t('App.Labels.Cancel')}
        silentSuccess
      />
    </>
  );
}
